import React from 'react';
import styled from '@emotion/styled';
import { AlertCircle } from 'lucide-react';

const ContentSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContentTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const OverrideTypeGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const OverrideTypeLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.checked ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  background: ${props => props.checked ? '#eff6ff' : 'white'};
  transition: all 0.2s;
  
  &:hover {
    border-color: #3b82f6;
  }
`;

const Checkbox = styled.input`
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

const FieldOverridesSection = ({ 
  error,
  createdBy,
  setCreatedBy,
  overrideTypes,
  onOverrideTypeChange,
  overrideValues,
  onValueChange,
  comment,
  setComment,
  renderStatusBar 
}) => {
  return (
    <>
      {renderStatusBar && renderStatusBar()}
      {error && (
        <ErrorMessage>
          <AlertCircle size={16} />
          {error}
        </ErrorMessage>
      )}

      <ContentSection>
        <ContentTitle>Override Field Values</ContentTitle>
        <FormGroup>
          <Label>Created By</Label>
          <Input
            type="text"
            value={createdBy}
            onChange={(e) => setCreatedBy(e.target.value)}
            placeholder="Enter your name"
          />
        </FormGroup>

        <FormGroup>
          <Label>Select Override Types</Label>
          <OverrideTypeGroup>
            <OverrideTypeLabel checked={overrideTypes.value}>
              <Checkbox
                type="checkbox"
                checked={overrideTypes.value}
                onChange={() => onOverrideTypeChange('value')}
              />
              <span>Value</span>
            </OverrideTypeLabel>
            <OverrideTypeLabel checked={overrideTypes.master_value}>
              <Checkbox
                type="checkbox"
                checked={overrideTypes.master_value}
                onChange={() => onOverrideTypeChange('master_value')}
              />
              <span>Master Value</span>
            </OverrideTypeLabel>
            <OverrideTypeLabel checked={overrideTypes.source}>
              <Checkbox
                type="checkbox"
                checked={overrideTypes.source}
                onChange={() => onOverrideTypeChange('source')}
              />
              <span>Source</span>
            </OverrideTypeLabel>
          </OverrideTypeGroup>
        </FormGroup>

        {overrideTypes.value && (
          <FormGroup>
            <Label>Override Value</Label>
            <Input
              type="text"
              value={overrideValues.value}
              onChange={(e) => onValueChange('value', e.target.value)}
              placeholder="Enter new value"
            />
          </FormGroup>
        )}

        {overrideTypes.master_value && (
          <FormGroup>
            <Label>Override Master Value</Label>
            <Input
              type="text"
              value={overrideValues.master_value}
              onChange={(e) => onValueChange('master_value', e.target.value)}
              placeholder="Enter new master value"
            />
          </FormGroup>
        )}

        {overrideTypes.source && (
          <FormGroup>
            <Label>Override Source</Label>
            <Select
              value={overrideValues.source}
              onChange={(e) => onValueChange('source', e.target.value)}
            >
              <option value="">Select source</option>
              <option value="PROPOSAL">PROPOSAL</option>
              <option value="NOTE">NOTE</option>
              <option value="SECURITY AGREEMENT">SECURITY AGREEMENT</option>
              <option value="MASTER">MASTER</option>
            </Select>
          </FormGroup>
        )}

        <FormGroup>
          <Label>Comment (Optional)</Label>
          <TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment about this override..."
          />
        </FormGroup>
      </ContentSection>
    </>
  );
};

export default FieldOverridesSection;
